import { __extends } from "tslib";
import { getShadowElement, removeElement } from '@tyler-components-web/core';
import { deepQuerySelectorAll } from 'shadow-utils';
import { BACKDROP_CONSTANTS } from '../backdrop';
import { CHECKBOX_CONSTANTS } from '../checkbox';
import { toggleClass } from '../core';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { ICON_BUTTON_CONSTANTS } from '../icon-button';
import { RADIO_CONSTANTS } from '../radio';
import { SWITCH_CONSTANTS } from '../switch';
import { BOTTOM_SHEET_CONSTANTS } from './bottom-sheet-constants';
/**
 * Provides facilities for interacting with the internal DOM of `BottomSheetComponent`.
 */
var BottomSheetAdapter = /** @class */ (function (_super) {
    __extends(BottomSheetAdapter, _super);
    function BottomSheetAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._backdropElement = getShadowElement(component, BOTTOM_SHEET_CONSTANTS.selectors.BACKDROP);
        _this._containerElement = getShadowElement(component, BOTTOM_SHEET_CONSTANTS.selectors.CONTAINER);
        return _this;
    }
    BottomSheetAdapter.prototype.initializeAccessibility = function () {
        if (!this._component.hasAttribute('role')) {
            this._component.setAttribute('role', 'dialog');
            this._component.setAttribute('aria-modal', 'true');
        }
    };
    BottomSheetAdapter.prototype.setVisibility = function (visible) {
        toggleClass(this._containerElement, visible, BOTTOM_SHEET_CONSTANTS.classes.OPEN);
    };
    BottomSheetAdapter.prototype.attach = function () {
        var hostElement = document.querySelector(BOTTOM_SHEET_CONSTANTS.selectors.HOST) || document.body;
        hostElement.appendChild(this._component);
    };
    BottomSheetAdapter.prototype.detach = function () {
        removeElement(this._component);
    };
    BottomSheetAdapter.prototype.registerTransitionEndHandler = function (handler) {
        this._containerElement.addEventListener('transitionend', handler);
    };
    BottomSheetAdapter.prototype.deregisterTransitionEndHandler = function (handler) {
        this._containerElement.removeEventListener('transitionend', handler);
    };
    BottomSheetAdapter.prototype.setBodyListener = function (type, listener, options) {
        document.body.addEventListener(type, listener, options);
    };
    BottomSheetAdapter.prototype.removeBodyListener = function (type, listener) {
        document.body.removeEventListener(type, listener);
    };
    BottomSheetAdapter.prototype.setDocumentListener = function (type, listener) {
        document.addEventListener(type, listener);
    };
    BottomSheetAdapter.prototype.removeDocumentListener = function (type, listener) {
        document.removeEventListener(type, listener);
    };
    BottomSheetAdapter.prototype.registerBackdropClickHandler = function (handler) {
        this._backdropElement.addEventListener(BACKDROP_CONSTANTS.events.BACKDROP_CLICK, handler);
    };
    BottomSheetAdapter.prototype.deregisterBackdropClickHandler = function (handler) {
        this._backdropElement.removeEventListener(BACKDROP_CONSTANTS.events.BACKDROP_CLICK, handler);
    };
    BottomSheetAdapter.prototype.getOpenBottomSheets = function (selector) {
        return document.querySelectorAll(selector);
    };
    BottomSheetAdapter.prototype.trySetInitialFocus = function () {
        var elements = deepQuerySelectorAll(this._component, BOTTOM_SHEET_CONSTANTS.selectors.INITIAL_FOCUS);
        if (elements && elements.length) {
            var initialElement = elements[elements.length - 1];
            initialElement.focus();
        }
    };
    BottomSheetAdapter.prototype.isScrollable = function () {
        var contentElement = this._getContentElement();
        return contentElement ? contentElement.scrollHeight > contentElement.offsetHeight : false;
    };
    BottomSheetAdapter.prototype.isContentChild = function (el) {
        var contentElement = this._getContentElement();
        return contentElement ? contentElement.contains(el) : false;
    };
    BottomSheetAdapter.prototype.initScrollable = function () {
        var isScrollable = this.isScrollable();
        toggleClass(this._component, isScrollable, BOTTOM_SHEET_CONSTANTS.classes.SCROLLABLE);
        toggleClass(this._containerElement, isScrollable, BOTTOM_SHEET_CONSTANTS.classes.SCROLLABLE);
    };
    BottomSheetAdapter.prototype.setDragging = function (isDragging) {
        toggleClass(this._containerElement, isDragging, BOTTOM_SHEET_CONSTANTS.classes.DRAGGING);
        toggleClass(this._component, isDragging, BOTTOM_SHEET_CONSTANTS.classes.DRAGGING);
    };
    BottomSheetAdapter.prototype.setBackdropVisiblity = function (value) {
        this._backdropElement.hidden = !value;
    };
    BottomSheetAdapter.prototype.setFullscreen = function (value) {
        toggleClass(this._containerElement, value, BOTTOM_SHEET_CONSTANTS.classes.FULLSCREEN);
    };
    BottomSheetAdapter.prototype.tryLayoutChildren = function () {
        var layoutChildren = [ICON_BUTTON_CONSTANTS.elementName, SWITCH_CONSTANTS.elementName, CHECKBOX_CONSTANTS.elementName, RADIO_CONSTANTS.elementName];
        var commonLayoutElements = Array.from(this._component.querySelectorAll(layoutChildren.join(',')));
        commonLayoutElements.filter(function (el) { return typeof el.layout === 'function'; }).forEach(function (el) { return el.layout(); });
    };
    BottomSheetAdapter.prototype.setContainerHeight = function (height) {
        if (height !== null) {
            var percent = Math.min(100, 100 * height / window.innerHeight);
            this._containerElement.style.maxHeight = percent + "%";
        }
        else {
            this._containerElement.style.removeProperty('max-height');
        }
    };
    BottomSheetAdapter.prototype.getContainerBounds = function () {
        return this._containerElement.getBoundingClientRect();
    };
    BottomSheetAdapter.prototype.setDragTargetHandler = function (type, listener) {
        this._containerElement.addEventListener(type, listener);
    };
    BottomSheetAdapter.prototype.removeDragTargetHandler = function (type, listener) {
        this._containerElement.removeEventListener(type, listener);
    };
    BottomSheetAdapter.prototype.setBodyScrollHandler = function (listener) {
        var contentElement = this._getContentElement();
        if (contentElement) {
            contentElement.addEventListener('scroll', listener);
        }
    };
    BottomSheetAdapter.prototype._getContentElement = function () {
        var contentElements = Array.from(deepQuerySelectorAll(this._component, BOTTOM_SHEET_CONSTANTS.selectors.CONTENT_BODY, false));
        return contentElements.length ? contentElements[0] : null;
    };
    return BottomSheetAdapter;
}(BaseAdapter));
export { BottomSheetAdapter };
